import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { SupplierOfficeForm } from "@/app/Offices/Forms/SupplierOfficeForm";
import { UpdateSupplierOfficeInput } from "@/gql/graphql";
import { officeValidation } from "@/app/Offices/Validators/officeValidation";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateSupplierOfficeMutation } from "@/app/Offices/GraphQL/supplierOfficeMutations";
import { useOfficeRecord } from "@/app/Offices/Hooks/useOfficeRecord";
import { ContactInput } from "@/app/Contacts/Components/ContactInput";
import { ImoovaImage } from "@/app/Media/Components/Image";
import { OfficeNotificationForm } from "@/app/Offices/Forms/OfficeNotificationForm";
import { InfoBanner } from "@/lib/Components/Banners/InfoBanner";

export function OfficeDetailsScreen() {
  const office = useOfficeRecord();
  const { mutateAsync } = useGqlMutation(updateSupplierOfficeMutation);

  return (
    <div className="space-y-12 py-10">
      <RecordScreenFormCard<UpdateSupplierOfficeInput>
        onSubmit={(values) => {
          return mutateAsync({
            id: office.id,
            input: values,
          });
        }}
        validationSchema={toFormikValidationSchema(officeValidation)}
        title="Details"
        initialValues={{
          name: office.name,
          code: office.code,
          city: {
            connect: office.city.id,
          },
          supplier: {
            connect: office.supplier.id,
          },
          address: {
            upsert: {
              id: office.address?.id,
              address_line_1: office.address?.address_line_1,
              address_line_2: office.address?.address_line_2,
              city: office.address?.city,
              state: office.address?.state,
              country: office.address?.country,
              postcode: office.address?.postcode,
            },
          },
          email: office.email,
          phone: office.phone,
          pickup_instructions: office.pickup_instructions,
        }}
      >
        <SupplierOfficeForm
          initialLat={office.address?.lat}
          initialLng={office.address?.lng}
        />
      </RecordScreenFormCard>

      <RecordScreenFormCard<UpdateSupplierOfficeInput>
        initialValues={{
          billing_entity_id: office.billing_entity_id,
        }}
        onSubmit={(values) => {
          return mutateAsync({
            id: office.id,
            input: values,
          });
        }}
        title="Billing"
      >
        <InfoBanner className="col-span-full">
          Add different billing entities for relocations delivering to this
          office. The account default is{" "}
          <strong>
            {office.supplier.billingEntity?.legal_name ??
              office.supplier.billingEntity?.name ??
              "--"}
          </strong>
        </InfoBanner>

        <ContactInput
          name="billing_entity_id"
          label="Custom billing entity"
          tooltip="Add a different billing entity for relocations delivering to this
            office"
          getQueryVariables={(search) => {
            return {
              page: 1,
              first: 20,
              search,
              managedById: office.supplier.managedBy?.id,
            };
          }}
        />
      </RecordScreenFormCard>

      <RecordScreenFormCard<UpdateSupplierOfficeInput>
        title="Notifications"
        onSubmit={(values) => {
          return mutateAsync({
            id: office.id,
            input: values,
          });
        }}
        initialValues={{
          booking_email: office.booking_email,
          cc_booking_to_departure_office: office.cc_booking_to_departure_office,
        }}
      >
        <OfficeNotificationForm />
      </RecordScreenFormCard>

      {office.address.locationMedia ? (
        <ImoovaImage
          media={office.address.locationMedia}
          alt={`image of ${office.name}`}
        />
      ) : null}
    </div>
  );
}
